import { LucideIcon } from "lucide-react";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";

interface CapabilityItem {
  title: string;
  description: string;
  icon: LucideIcon;
  examples: string[];
  gradient: string;
}

interface CapabilitiesGridProps {
  badge: {
    icon: LucideIcon;
    text: string;
    color: string;
  };
  title: string;
  titleHighlight: string;
  subtitle: string;
  items: CapabilityItem[];
  hoverColor?: string;
}

export const CapabilitiesGrid = ({
  badge,
  title,
  titleHighlight,
  subtitle,
  items,
  hoverColor = "blue",
}: CapabilitiesGridProps) => {
  const BadgeIcon = badge.icon;

  const colorClasses: Record<string, { badge: string; title: string; hover: string }> = {
    blue: {
      badge: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      title: "text-blue-600 dark:text-blue-400",
      hover: "hover:border-blue-500/30 hover:shadow-blue-500/5 group-hover:text-blue-600 dark:group-hover:text-blue-400",
    },
    sky: {
      badge: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
      title: "text-sky-600 dark:text-sky-400",
      hover: "hover:border-sky-500/30 hover:shadow-sky-500/5 group-hover:text-sky-600 dark:group-hover:text-sky-400",
    },
    emerald: {
      badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      title: "text-emerald-600 dark:text-emerald-400",
      hover: "hover:border-emerald-500/30 hover:shadow-emerald-500/5 group-hover:text-emerald-600 dark:group-hover:text-emerald-400",
    },
    green: {
      badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      title: "text-emerald-600 dark:text-emerald-400",
      hover: "hover:border-emerald-500/30 hover:shadow-emerald-500/5 group-hover:text-emerald-600 dark:group-hover:text-emerald-400",
    },
    purple: {
      badge: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      title: "text-purple-600 dark:text-purple-400",
      hover: "hover:border-purple-500/30 hover:shadow-purple-500/5 group-hover:text-purple-600 dark:group-hover:text-purple-400",
    },
  };

  const colors = colorClasses[hoverColor] || colorClasses.blue;

  return (
    <section className="py-12 md:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 md:mb-12">
            <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 ${colors.badge}`}>
              <BadgeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>{badge.text}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4 px-2">
              {title} <span className={colors.title}>{titleHighlight}</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-muted-foreground px-2">{subtitle}</p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 max-w-6xl mx-auto">
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            return (
              <AnimateOnScroll key={index} animation="fade-up" delay={index * 100}>
                <div className={`group p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl bg-card border border-border hover:shadow-lg transition-all duration-300 h-full flex flex-col ${colors.hover.split(" ").slice(0, 2).join(" ")}`}>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}>
                    <ItemIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white" />
                  </div>
                  <h3 className={`text-base sm:text-lg md:text-xl font-semibold text-foreground mb-1.5 sm:mb-2 transition-colors ${colors.hover.split(" ").slice(2).join(" ")}`}>
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4 flex-grow">{item.description}</p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {item.examples.map((example, i) => (
                      <span key={i} className="px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full bg-muted text-muted-foreground">
                        {example}
                      </span>
                    ))}
                  </div>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};
